import { useState, useEffect, useRef } from "react";

const SUPABASE_FUNCTION_URL = "https://laenbifgqogykzsfhzoa.supabase.co/functions/v1/brevo-contact";
const SUPABASE_ANON_KEY = "sb_publishable_Tp2TlMz9Wa_GL8pvddUaIQ_bzmWQXZE";

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorDetail, setErrorDetail] = useState<string>("");
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll(".animate-on-scroll");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setErrorDetail("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const message = formData.get("message") as string;

    console.log("[ContactForm] Submitting:", { name, email, messageLength: message?.length });

    try {
      const response = await fetch(SUPABASE_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ name, email, message }),
      });

      console.log("[ContactForm] Response status:", response.status);

      const result = await response.json();
      console.log("[ContactForm] Response body:", result);

      if (!response.ok) {
        console.error("[ContactForm] Edge Function error:", result);
        setFormStatus("error");
        setErrorDetail(result?.detail || result?.error || "Erreur inconnue");
        return;
      }

      setFormStatus("success");
      form.reset();
    } catch (err) {
      console.error("[ContactForm] Network error:", err);
      setFormStatus("error");
      setErrorDetail(String(err));
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-14 md:py-20 bg-background-50">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-6 transition-all duration-700">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading">
              Contactez-nous
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto rounded-full"></div>
            <p className="mt-6 text-foreground-50">
              Vous avez un projet d'événement ? N'hésitez pas à nous contacter pour discuter de vos idées et besoins.
            </p>
          </div>
          {formStatus === "success" ? (
            <div className="opacity-100">
              <div className="bg-background-100 border border-primary-500/20 rounded-2xl p-10 md:p-14 text-center">
                <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-3xl text-primary-500"></i>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 font-heading text-foreground-50">
                  Votre projet a bien été envoyé.
                </h3>
                <p className="text-foreground-400 leading-relaxed max-w-lg mx-auto">
                  Merci pour votre confiance. Nous reviendrons vers vous prochainement afin d'échanger sur votre projet et vous accompagner dans la création de votre événement.
                </p>
              </div>
            </div>
          ) : (
            <form
              id="melio-contact"
              onSubmit={handleSubmit}
              className="space-y-6 opacity-100"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-50 mb-2">
                    Nom
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border-2 border-foreground-300/50 text-foreground-50 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-50 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border-2 border-foreground-300/50 text-foreground-50 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-50 mb-2">
                  Décrivez votre projet
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border-2 border-foreground-300/50 text-foreground-50 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                  placeholder="Décrivez-nous votre projet ou simplement votre idée, nous vous accompagnerons dans sa concrétisation."
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">
                  Maximum 500 caractères
                </p>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="bg-primary-500 text-background-50 font-semibold px-8 py-3 rounded-lg whitespace-nowrap hover:bg-primary-600 transition-all duration-300 w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {formStatus === "submitting" ? "Envoi en cours..." : "Envoyer mon projet"}
                </button>
              </div>
              {formStatus === "error" && (
                <div className="bg-foreground-500/10 border border-foreground-500/30 text-foreground-50 px-4 py-3 rounded-lg">
                  Une erreur s'est produite. Veuillez réessayer plus tard.
                  {errorDetail && (
                    <p className="text-xs mt-1 opacity-70 break-all">{errorDetail}</p>
                  )}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}